/**
 * Haptic feedback utility for mobile devices
 * Uses the Vibration API where available
 */

type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error";

const vibrationPatterns: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 10],
  warning: [25, 50, 25],
  error: [50, 100, 50],
};

/**
 * Trigger haptic feedback if supported
 */
export const triggerHaptic = (style: HapticStyle = "light"): void => {
  if (typeof navigator === "undefined" || !("vibrate" in navigator)) {
    return;
  }

  try {
    const pattern = vibrationPatterns[style];
    navigator.vibrate(pattern);
  } catch (e) {
    // Silently fail - haptics are non-essential
    console.debug("[Haptics] Vibration failed:", e);
  }
};

/**
 * Check if haptic feedback is supported
 */
export const isHapticSupported = (): boolean => {
  return typeof navigator !== "undefined" && "vibrate" in navigator;
};

/**
 * Hook into button clicks to add haptic feedback
 * Call this in a useEffect or attach to onClick handlers
 */
export const withHaptic = <T extends (...args: unknown[]) => unknown>(
  fn: T,
  style: HapticStyle = "light"
): T => {
  return ((...args: Parameters<T>) => {
    triggerHaptic(style);
    return fn(...args);
  }) as T;
};
